import React, { useState, useEffect } from 'react';
import { Shield, Check, Trash2, Clock, CheckCircle, Lock, LogOut, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = 'Masterplan';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [pendingEntries, setPendingEntries] = useState([]);
  const [approvedEntries, setApprovedEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);

  // Check session storage for existing auth
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('plomari_admin_auth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch entries when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllEntries();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('plomari_admin_auth', 'true');
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('plomari_admin_auth');
    setPassword('');
  };

  const fetchAllEntries = async () => {
    setLoading(true);
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        fetch(`${API_URL}/api/guestbook/pending`),
        fetch(`${API_URL}/api/guestbook`)
      ]);
      
      if (pendingRes.ok) {
        const pending = await pendingRes.json();
        setPendingEntries(pending);
      }
      
      if (approvedRes.ok) {
        const approved = await approvedRes.json();
        setApprovedEntries(approved);
      }
    } catch (err) {
      console.error('Error fetching entries:', err);
      showMessage('Failed to fetch entries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setActionMessage({ text, type });
    setTimeout(() => setActionMessage(null), 3000);
  };

  const approveEntry = async (entryId) => {
    try {
      const res = await fetch(`${API_URL}/api/guestbook/${entryId}/approve`, {
        method: 'PUT'
      });
      
      if (res.ok) {
        showMessage('Entry approved successfully!');
        fetchAllEntries();
      } else {
        showMessage('Failed to approve entry', 'error');
      }
    } catch (err) {
      showMessage('Error approving entry', 'error');
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry? This cannot be undone.')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/guestbook/${entryId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        showMessage('Entry deleted successfully!');
        fetchAllEntries();
      } else {
        showMessage('Failed to delete entry', 'error');
      }
    } catch (err) {
      showMessage('Error deleting entry', 'error');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="admin-login-page" data-testid="admin-login-page">
        <div className="admin-login-container">
          <div className="admin-login-card">
            <div className="admin-login-header">
              <Shield size={48} className="admin-shield-icon" />
              <h1>Royal Administration</h1>
              <p>Enter the password to access the Guestbook moderation panel</p>
            </div>
            
            <form onSubmit={handleLogin} className="admin-login-form">
              <div className="admin-input-group">
                <Lock size={20} className="input-icon" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="admin-password-input"
                  data-testid="admin-password-input"
                  autoFocus
                />
              </div>
              
              {passwordError && (
                <div className="admin-error" data-testid="admin-error">
                  {passwordError}
                </div>
              )}
              
              <button 
                type="submit" 
                className="admin-login-btn"
                data-testid="admin-login-btn"
              >
                Enter the Kingdom
              </button>
            </form>
            
            <a href="/" className="admin-back-link">Back to main site</a>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="admin-page" data-testid="admin-page">
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-title">
            <Shield size={28} />
            <h1>Guestbook Moderation</h1>
          </div>
          <div className="admin-actions">
            <button 
              onClick={fetchAllEntries} 
              className="admin-refresh-btn"
              disabled={loading}
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={18} className={loading ? 'spinning' : ''} />
              Refresh
            </button>
            <button 
              onClick={handleLogout} 
              className="admin-logout-btn"
              data-testid="admin-logout-btn"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {actionMessage && (
        <div className={`admin-message ${actionMessage.type}`} data-testid="admin-message">
          {actionMessage.text}
        </div>
      )}

      <main className="admin-main">
        {/* Pending Entries Section */}
        <section className="admin-section" data-testid="pending-section">
          <div className="section-header">
            <Clock size={24} />
            <h2>Pending Approval</h2>
            <span className="entry-count">{pendingEntries.length}</span>
          </div>
          
          {loading ? (
            <div className="admin-loading">Loading entries...</div>
          ) : pendingEntries.length === 0 ? (
            <div className="admin-empty">
              <p>No pending entries to review</p>
            </div>
          ) : (
            <div className="entries-list">
              {pendingEntries.map((entry) => (
                <div key={entry.id} className="admin-entry-card pending" data-testid="pending-entry">
                  <div className="entry-header">
                    <div className="entry-info">
                      <span className="entry-name">{entry.name}</span>
                      {entry.location && (
                        <span className="entry-location">{entry.location}</span>
                      )}
                    </div>
                    <span className="entry-date">{formatDate(entry.created_at)}</span>
                  </div>
                  <p className="entry-message">{entry.message}</p>
                  <div className="entry-actions">
                    <button 
                      onClick={() => approveEntry(entry.id)}
                      className="approve-btn"
                      data-testid="approve-btn"
                    >
                      <Check size={16} />
                      Approve
                    </button>
                    <button 
                      onClick={() => deleteEntry(entry.id)}
                      className="delete-btn"
                      data-testid="delete-btn"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Approved Entries Section */}
        <section className="admin-section" data-testid="approved-section">
          <div className="section-header">
            <CheckCircle size={24} />
            <h2>Approved Entries</h2>
            <span className="entry-count">{approvedEntries.length}</span>
          </div>
          
          {loading ? (
            <div className="admin-loading">Loading entries...</div>
          ) : approvedEntries.length === 0 ? (
            <div className="admin-empty">
              <p>No approved entries yet</p>
            </div>
          ) : (
            <div className="entries-list">
              {approvedEntries.map((entry) => (
                <div key={entry.id} className="admin-entry-card approved" data-testid="approved-entry">
                  <div className="entry-header">
                    <div className="entry-info">
                      <span className="entry-name">{entry.name}</span>
                      {entry.location && (
                        <span className="entry-location">{entry.location}</span>
                      )}
                    </div>
                    <span className="entry-date">{formatDate(entry.created_at)}</span>
                  </div>
                  <p className="entry-message">{entry.message}</p>
                  <div className="entry-actions">
                    <button 
                      onClick={() => deleteEntry(entry.id)}
                      className="delete-btn"
                      data-testid="delete-approved-btn"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <footer className="admin-footer">
        <p>Kingdom of Plomari - Guestbook Administration</p>
      </footer>
    </div>
  );
};

export default AdminPage;
